import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { useLanguageLink } from '../../hooks/useLanguageLink';

interface CompliantLayoutProps {
  children: ReactNode;
}

/**
 * Minimal, self-contained layout for paid-ad landing pages.
 *
 * Deliberately does NOT link to the main marketing site (home, features,
 * blog, comparisons, pricing) because those pages describe traffic-cloaking,
 * which violates Google Ads policy. A reviewer who reaches cloaking content
 * from an ad's landing page — even a few clicks deep through shared nav — can
 * suspend the account. Every outbound link here stays inside the compliant
 * funnel: the sign-up flow (AuthLayout) and legal pages (this layout).
 *
 * Only route policy-clean pages through this layout.
 */
export default function CompliantLayout({ children }: CompliantLayoutProps) {
  const { t } = useTranslation(['marketing', 'common']);
  const { isAuthenticated } = useAuthStore();
  const { createLink } = useLanguageLink();
  const currentYear = new Date().getFullYear();

  return (
    <div id="top" className="min-h-screen flex flex-col bg-white">
      {/* Header — no navigation into the marketing/cloaking site */}
      <header className="public-header">
        <div className="container-marketing">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo scrolls to top of the current landing page only */}
            <a href="#top" className="flex items-center space-x-3">
              <img src="/AxetraLogo.webp" alt="Axetra" className="h-8 md:h-10" />
              <span className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                Axetra
              </span>
            </a>

            <div className="flex items-center space-x-4">
              <LanguageSwitcher />

              {isAuthenticated ? (
                <Link to="/dashboard" className="btn btn-primary">
                  {t('navigation.dashboard', { ns: 'common' })}
                </Link>
              ) : (
                <>
                  <Link
                    to={createLink('login')}
                    className="hidden sm:inline-block text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    {t('auth.login', { ns: 'common' })}
                  </Link>
                  <Link to={createLink('register')} className="btn btn-primary">
                    {t('hero.cta_primary', { ns: 'marketing' })}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer — legal links only; these routes also use CompliantLayout */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="container-marketing py-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-2">
              <img src="/AxetraLogo.webp" alt="Axetra" className="h-6" />
              <span className="text-xs text-gray-500">
                {t('footer.tagline', { ns: 'marketing' })}
              </span>
            </div>

            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <Link to={createLink('privacy')} className="text-sm hover:text-white transition-colors">
                {t('footer.legal_privacy', { ns: 'marketing' })}
              </Link>
              <Link to={createLink('terms')} className="text-sm hover:text-white transition-colors">
                {t('footer.legal_terms', { ns: 'marketing' })}
              </Link>
              <Link to={createLink('cookies')} className="text-sm hover:text-white transition-colors">
                {t('footer.legal_cookies', { ns: 'marketing' })}
              </Link>
            </nav>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-800 text-center">
            <p className="text-sm text-gray-400">
              {t('footer.copyright', { year: currentYear, ns: 'marketing' })}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
